import { Destination, DestinationFilters } from '../types/Destination';
import { getApiHeaders } from '../utils/apiHeaders';

const API_BASE_URL = process.env.REACT_APP_TRAVEL_API_BASE_URL;

export type DestinationPayload = Destination;

export interface DestinationUploadBundle {
  heroImageFile?: File | null;
  seoOgImageFile?: File | null;
  galleryImageFiles?: Array<File | null>;
  recommendedExperienceImageFiles?: Array<File | null>;
}

const withAuthHeaders = () => getApiHeaders('json');

const buildQueryString = (filters: DestinationFilters = {}) => {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.updatedAt) params.append('updated_at', filters.updatedAt);
  if (filters.createdBy !== undefined && filters.createdBy !== null && String(filters.createdBy).trim() !== '') {
    params.append('created_by', String(filters.createdBy));
  }
  if (typeof filters.publishedOnly === 'boolean') {
    params.append('published_only', String(filters.publishedOnly));
  }
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

const ensureResponse = async (response: Response) => {
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Destinations API error: ${response.status}`);
  }
  return response.json();
};

export const fetchDestinations = async (filters: DestinationFilters = {}) => {
  const query = buildQueryString(filters);
  const res = await fetch(`${API_BASE_URL}/destinations${query}`, {
    headers: withAuthHeaders()
  });
  return ensureResponse(res) as Promise<Destination[]>;
};

export const getDestinationFull = async (slug: string) => {
  const res = await fetch(`${API_BASE_URL}/destinations/${encodeURIComponent(slug)}`, {
    headers: withAuthHeaders()
  });
  return ensureResponse(res) as Promise<Destination>;
};

export const createDestination = async (payload: DestinationPayload, uploads?: DestinationUploadBundle) => {
  return createOrUpdateDestination(`${API_BASE_URL}/destinations`, 'POST', payload, uploads);
};

export const updateDestination = async (slug: string, payload: Partial<DestinationPayload>, uploads?: DestinationUploadBundle) => {
  return createOrUpdateDestination(`${API_BASE_URL}/destinations/${encodeURIComponent(slug)}`, 'PATCH', payload, uploads);
};

const createOrUpdateDestination = async (
  url: string,
  method: 'POST' | 'PATCH',
  payload: Partial<DestinationPayload>,
  uploads?: DestinationUploadBundle
) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
      return;
    }
    formData.append(key, String(value));
  });

  if (uploads?.heroImageFile) {
    formData.append('heroImageFile', uploads.heroImageFile);
  }
  if (uploads?.seoOgImageFile) {
    formData.append('seoOgImageFile', uploads.seoOgImageFile);
  }
  uploads?.galleryImageFiles?.forEach((file, index) => {
    if (file) formData.append(`galleryImageFiles[${index}]`, file);
  });
  uploads?.recommendedExperienceImageFiles?.forEach((file, index) => {
    if (file) formData.append(`recommendedExperienceImageFiles[${index}]`, file);
  });

  const res = await fetch(url, {
    method,
    headers: getApiHeaders('form-data'),
    body: formData
  });
  return ensureResponse(res) as Promise<Destination>;
};

export const deleteDestination = async (slug: string) => {
  const res = await fetch(`${API_BASE_URL}/destinations/${encodeURIComponent(slug)}`, {
    method: 'DELETE',
    headers: withAuthHeaders()
  });
  return ensureResponse(res);
};
